import { Autocomplete, Box, styled } from "@mui/material";

const COMPACT_FIELD = {
  labelMinHeight: "32px",
  inputHeight: "48px",
  tableInputHeight: "44px",
  helperMinHeight: "16px",
  labelMarginBottom: "4px",
};

const compactLabelSx = (theme) => ({
  fontSize: theme.typography.regular.fontSize,
  fontWeight: theme.typography.fontWeight.medium,
  minHeight: COMPACT_FIELD.labelMinHeight,
  lineHeight: 1.25,
  marginBottom: COMPACT_FIELD.labelMarginBottom,
  display: "flex",
  alignItems: "flex-end",
  color: theme.palette.custom.productTextColor,
});

const compactHelperSx = {
  minHeight: COMPACT_FIELD.helperMinHeight,
  margin: "3px 0 0",
  lineHeight: COMPACT_FIELD.helperMinHeight,
  display: "block",
};

const compactOutlinedInputSx = (theme, height = COMPACT_FIELD.inputHeight) => ({
  height,
  minHeight: height,
  maxHeight: height,
  borderRadius: theme.borderRadius.radius.wallet,
  background: theme.palette.custom.white,
  backgroundColor: theme.palette.custom.white,
  boxSizing: "border-box",
  padding: "0 10px !important",
  "&.Mui-error": {
    background: theme.palette.custom.white,
    backgroundColor: theme.palette.custom.white,
  },
  "& .MuiAutocomplete-input": {
    padding: "0 !important",
    backgroundColor: "transparent",
  },
  "& .MuiInputBase-input": {
    padding: "0 !important",
    height: "24px",
    minHeight: "24px",
    lineHeight: "24px",
    boxSizing: "border-box",
    backgroundColor: "transparent",
  },
  "& .MuiAutocomplete-endAdornment": {
    top: "50%",
    transform: "translateY(-50%)",
    right: "8px",
  },
});

export const StyledBoxLabel = styled(Box, {
  shouldForwardProp: (prop) => prop !== "formGrid",
})(({ theme, color, fontSize, formGrid }) => ({
  fontSize: fontSize ? fontSize : theme.typography.extraLarge.fontSize,
  fontWeight: theme.typography.fontWeight.medium,
  marginBottom: "6px",
  color: color || theme.palette.custom.productTextColor,
  flexShrink: 0,
  ...(formGrid ? compactLabelSx(theme) : {}),
}));
export const StyledLabelSpan = styled("span")(({ theme }) => ({
  color: theme.palette.custom.danger,
}));
export const StyledBox = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "isprofile" &&
    prop !== "matchAddressInput" &&
    prop !== "formGrid" &&
    prop !== "tableField" &&
    prop !== "profileForm",
})(({ isprofile, formGrid, tableField, profileForm }) => ({
  paddingTop:
    formGrid || tableField || profileForm
      ? "0px"
      : isprofile
        ? "35px"
        : "20px",
  width: "100%",
  background: "transparent",
  ...(formGrid && {
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
  }),
}));
export const StyledAutocomplete = styled(Autocomplete, {
  shouldForwardProp: (prop) =>
    prop !== "isprofile" &&
    prop !== "matchAddressInput" &&
    prop !== "formGrid" &&
    prop !== "tableField" &&
    prop !== "profileForm",
})(({ theme, isprofile, formGrid, matchAddressInput, tableField, profileForm }) => {
  const useCompactLayout = isprofile || formGrid || tableField;
  /** Profile address page only — formGrid/tableField use fixed 48px / 44px */
  const useAddressPageSizing = matchAddressInput && !formGrid && !tableField;
  const fieldHeight = tableField
    ? COMPACT_FIELD.tableInputHeight
    : COMPACT_FIELD.inputHeight;

  return {
    width: "100%",
    minWidth: useAddressPageSizing ? 0 : "120px",
    background: "transparent",
    "& .MuiAutocomplete-root": {
      background: "transparent",
    },
    ...(useCompactLayout && {
      "& .MuiOutlinedInput-root": compactOutlinedInputSx(theme, fieldHeight),
    }),
    "& .MuiOutlinedInput-root": {
      height: useAddressPageSizing
        ? "auto"
        : useCompactLayout
          ? undefined
          : "44px",
      minHeight: useAddressPageSizing ? "62px" : undefined,
      maxHeight: useCompactLayout && !useAddressPageSizing ? fieldHeight : undefined,
      borderRadius:
        useCompactLayout || useAddressPageSizing
          ? theme.borderRadius.radius.wallet
          : undefined,
      background: theme.palette.custom.white,
      backgroundColor: theme.palette.custom.white,
      alignItems: useAddressPageSizing ? "center" : undefined,
      padding: useAddressPageSizing ? "0 !important" : undefined,
      "&.Mui-error": {
        background: theme.palette.custom.white,
        backgroundColor: theme.palette.custom.white,
      },
    },
    "& .MuiInputBase-input": {
      fontSize: theme.typography.regular.fontSize,
      background: "transparent",
      backgroundColor: "transparent",
      ...(useCompactLayout && {
        height: "24px",
        minHeight: "24px",
        lineHeight: "24px",
        padding: "0 !important",
        boxSizing: "border-box",
      }),
      ...(useAddressPageSizing && {
        height: "28px",
        padding: "17px 10px !important",
        boxSizing: "content-box",
      }),
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
      ...(useCompactLayout ? compactHelperSx : { minHeight: "20px" }),
    },
  };
});
